/**
 * RegistrationScreen - Participant Registration Screen
 * 
 * Supports 3 registration modes:
 * - "none": Anonymous only (random names)
 * - "optional": Link to login shown, but can skip
 * - "required": Must be logged in to participate
 * 
 * Features:
 * - Random name generation
 * - Anonymous option
 * - Login requirement check for "required" mode
 * - Course auto-enrollment for logged-in users
 * - Dark mode support
 * 
 * Uses unifiedDataService for all RTDB operations.
 */

import React, { useState, useCallback } from 'react';
import { useTheme } from '../../../context/ThemeContext';
import { ThemeToggle } from '../../../context/ThemeContext';
import { useUnifiedAuth } from '../../../context/UnifiedAuthContext';
import { AuthModal } from '../../../components/auth';
import { RealtimeDBService, LiveSessionService } from '../../../firebase/unifiedDataService';
import { generateRandomName, generateParticipantId } from '../../../utils/randomNameGenerator';
import { Logger } from '../../../utils/logger';

const log = Logger.create('RegistrationScreen');
import { enrollStudentOnQuizJoin } from '../../../utils/studentStatsService';

/**
 * RegistrationScreen component
 * @param {string} sessionId - The resolved session ID
 * @param {string} shortCode - The short code (if provided)
 * @param {Object} settings - Quiz settings (for display)
 * @param {number} participantCount - Current number of participants
 * @param {string} instancePath - Full Firebase path for the instance
 * @param {Function} onRegistered - Callback when registration succeeds (id, name, studentId?)
 * @param {Function} onError - Callback when registration fails
 */
export function RegistrationScreen({
  sessionId,
  shortCode,
  settings = {},
  participantCount = 0,
  instancePath,
  onRegistered,
  onError,
}) {
  const { darkMode } = useTheme();
  const { user, userProfile, isAuthenticated } = useUnifiedAuth();
  
  // Generate random name on mount (always random, even for logged-in users)
  const [selectedName, setSelectedName] = useState(() => generateRandomName());
  const [enteredCode, setEnteredCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);

  // If no session ID, show code entry
  const needsCodeEntry = !sessionId;
  
  // Check if registration is required and user is not logged in
  const isRequiredAndNotLoggedIn = settings.studentRegistration === 'required' && !isAuthenticated;

  /**
   * Reroll for a new random name (only if not logged in)
   */
  const handleRerollName = () => {
    setSelectedName(generateRandomName());
  };

  /**
   * Set name to anonymous
   */
  const handleAnonymous = () => {
    setSelectedName('Anonym');
  };

  /**
   * Handle registration
   */
  const handleRegister = useCallback(async () => {
    if (!selectedName) {
      setError('Bitte wähle einen Namen');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Determine the correct path for participants
      let participantsPath = instancePath 
        ? `${instancePath}/participants`
        : `sessions/${sessionId}/participants`;

      // If no session ID, resolve from code first
      if (needsCodeEntry) {
        const code = enteredCode.trim().toUpperCase();
        if (!code) {
          setError('Bitte geben Sie einen Quiz-Code ein');
          setIsLoading(false);
          return;
        }

        // Look up session by short code using LiveSessionService
        const codeData = await LiveSessionService.getByCode(code);
        
        if (!codeData) {
          setError('Quiz-Code nicht gefunden');
          setIsLoading(false);
          return;
        }

        participantsPath = `quiz_instances/${codeData.userId}/${codeData.instanceId}/participants`;
      }

      // Check if name is already taken (only if not anonymous)
      if (selectedName !== 'Anonym') {
        const participants = await RealtimeDBService.get(participantsPath) || {};
        
        const nameTaken = Object.values(participants).some(
          p => p.name && p.name.toLowerCase() === selectedName.toLowerCase()
        );
        
        if (nameTaken) {
          setError('Dieser Name ist bereits vergeben. Bitte würfle einen neuen Namen.');
          setIsLoading(false);
          return;
        }
      }

      // Generate participant ID and register
      const participantId = generateParticipantId();
      const participantPath = `${participantsPath}/${participantId}`;
      
      // Build participant data
      // Use totalScore/currentStreak to match DB rules validation
      const participantData = {
        name: selectedName,
        joinedAt: Date.now(),
        totalScore: 0,
        currentStreak: 0,
      };
      
      // If user is authenticated, add student data
      if (isAuthenticated && user) {
        participantData.studentId = user.uid;
        participantData.isRegistered = true;
        participantData.registeredAt = Date.now();
        if (userProfile?.email) {
          participantData.email = userProfile.email;
        }
      }
      
      await RealtimeDBService.set(participantPath, participantData);

      // If user is authenticated and quiz is part of a course, auto-enroll
      if (isAuthenticated && user && settings.courseId && instancePath) {
        try {
          // Extract teacherUserId from instancePath (format: quiz_instances/{teacherUserId}/{instanceId})
          const pathParts = instancePath.split('/');
          const teacherUserId = pathParts[1]; // quiz_instances/[teacherUserId]/instanceId
          
          if (teacherUserId) {
            await enrollStudentOnQuizJoin({
              studentId: user.uid,
              teacherUserId: teacherUserId,
              courseId: settings.courseId,
              courseName: settings.courseName || 'Unnamed Course'
            });
          }
        } catch (enrollError) {
          log.error('Error enrolling student in course', enrollError);
          // Don't fail quiz join if enrollment fails
        }
      }

      // Success callback - pass studentId if authenticated
      onRegistered?.(participantId, selectedName, isAuthenticated ? user.uid : null);

    } catch (err) {
      log.error('Registration error', err);
      setError('Registrierung fehlgeschlagen. Bitte versuchen Sie es erneut.');
      onError?.(err);
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, instancePath, selectedName, enteredCode, needsCodeEntry, isAuthenticated, user, userProfile, onRegistered, onError]);

  /**
   * Handle Enter key
   */
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !isLoading) {
      handleRegister();
    }
  };

  return (
    <div className={`min-h-screen flex flex-col ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Header */}
      <header
        className={`
          py-4 px-6 flex items-center justify-between
          ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}
          border-b
        `}
      >
        <h1 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          Quiz beitreten
        </h1>
        <ThemeToggle />
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-6">
        <div
          className={`
            w-full max-w-md p-8 rounded-2xl shadow-xl
            ${darkMode ? 'bg-gray-800' : 'bg-white'}
          `}
        >
          {/* Icon */}
          <div className="text-center mb-6">
            <div
              className={`
                inline-flex items-center justify-center w-16 h-16 rounded-full
                ${darkMode ? 'bg-indigo-900/30' : 'bg-indigo-100'}
                mb-4
              `}
            >
              <svg
                className={`w-8 h-8 ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                />
              </svg>
            </div>

            <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Willkommen!
            </h2>
            
            {shortCode && (
              <p className={`mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Quiz-Code: <span className="font-mono font-bold">{shortCode}</span>
              </p>
            )}

            {participantCount > 0 && (
              <p className={`mt-1 text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                {participantCount} {participantCount === 1 ? 'Teilnehmer' : 'Teilnehmer'} bereits dabei
              </p>
            )}
          </div>

          {/* Code Entry (if needed) */}
          {needsCodeEntry && (
            <div className="mb-4">
              <label
                htmlFor="quiz-code"
                className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}
              >
                Quiz-Code
              </label>
              <input
                id="quiz-code"
                type="text"
                value={enteredCode}
                onChange={(e) => setEnteredCode(e.target.value.toUpperCase())}
                onKeyPress={handleKeyPress}
                placeholder="z.B. ABC123"
                maxLength={10}
                className={`
                  w-full px-4 py-3 rounded-xl text-center text-2xl font-mono font-bold tracking-widest
                  ${darkMode 
                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500' 
                    : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400'}
                  border-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
                  theme-transition
                `}
                autoComplete="off"
                autoFocus
              />
            </div>
          )}

          {/* Current Name Display */}
          <div 
            className={`
              mb-4 p-4 rounded-xl text-center
              ${selectedName === 'Anonym'
                ? darkMode 
                  ? 'bg-gray-700/50 border border-gray-600' 
                  : 'bg-gray-100 border border-gray-200'
                : darkMode
                  ? 'bg-gradient-to-r from-purple-900/30 to-blue-900/30 border border-purple-700'
                  : 'bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200'
              }
            `}
          >
            <p className={`text-sm mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Dein Name:
            </p>
            <p className={`text-2xl font-bold ${
              selectedName === 'Anonym'
                ? darkMode ? 'text-gray-300' : 'text-gray-600'
                : darkMode ? 'text-purple-300' : 'text-purple-800'
            }`}>
              {selectedName}
            </p>
          </div>

          {/* Name Selection Buttons */}
          <div className="flex gap-2 mb-6">
            {/* Reroll Button */}
            <button
              onClick={handleRerollName}
              disabled={isLoading}
              className={`
                flex-1 py-3 px-4 rounded-xl font-medium
                flex items-center justify-center gap-2
                theme-transition
                ${darkMode 
                  ? 'bg-purple-900/50 text-purple-300 hover:bg-purple-800/50' 
                  : 'bg-purple-100 text-purple-700 hover:bg-purple-200'}
                ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}
              `}
            >
              Neu würfeln
            </button>

            {/* Anonymous Button */}
            <button
              onClick={handleAnonymous}
              disabled={isLoading}
              className={`
                flex-1 py-3 px-4 rounded-xl font-medium
                flex items-center justify-center gap-2
                theme-transition
                ${selectedName === 'Anonym'
                  ? 'bg-gray-600 text-white'
                  : darkMode 
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}
                ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}
              `}
            >
              Anonym
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div
              className={`
                mb-4 p-3 rounded-lg text-center
                ${darkMode ? 'bg-red-900/30 text-red-400' : 'bg-red-100 text-red-700'}
              `}
            >
              {error}
            </div>
          )}
          
          {/* Required Login Warning */}
          {isRequiredAndNotLoggedIn && (
            <div
              className={`
                mb-4 p-4 rounded-lg border-2
                ${darkMode 
                  ? 'bg-orange-900/30 border-orange-700 text-orange-300' 
                  : 'bg-orange-50 border-orange-300 text-orange-800'}
              `}
            >
              <div className="flex items-start gap-3">
                <svg className="w-6 h-6 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div>
                  <p className="font-semibold mb-1">Anmeldung erforderlich</p>
                  <p className="text-sm">
                    Für dieses Quiz musst du angemeldet sein. Bitte klicke auf den Link unten, um dich anzumelden oder zu registrieren.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <button
            onClick={handleRegister}
            disabled={isLoading || (needsCodeEntry && !enteredCode.trim()) || isRequiredAndNotLoggedIn}
            className={`
              w-full py-4 px-6 rounded-xl font-semibold text-lg
              transition-all duration-200
              ${isLoading || (needsCodeEntry && !enteredCode.trim()) || isRequiredAndNotLoggedIn
                ? darkMode 
                  ? 'bg-gray-700 text-gray-500 cursor-not-allowed' 
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-green-600 text-white hover:bg-green-700 active:scale-[0.98]'
              }
              flex items-center justify-center gap-2
            `}
          >
            {isLoading ? (
              <>
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Beitreten...
              </>
            ) : (
              <>
                Quiz starten
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </>
            )}
          </button>

          {/* Quiz Info */}
          {settings.quizName && (
            <div className={`mt-6 pt-6 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <p className={`text-center text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Du trittst bei:
              </p>
              <p className={`text-center font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {settings.quizName}
              </p>
            </div>
          )}

          {/* Registration/Login Link or Logged-in Status */}
          {(settings.studentRegistration === 'optional' || settings.studentRegistration === 'required') && (
            <div className={`mt-6 pt-6 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              {isAuthenticated ? (
                /* Show logged-in status */
                <div className="text-center">
                  <div className={`
                    inline-flex items-center gap-2 px-4 py-2 rounded-lg
                    ${darkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-50 text-green-700'}
                  `}>
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="font-medium">
                      Angemeldet als {userProfile?.displayName || userProfile?.email || user?.email}
                    </span>
                  </div>
                  <p className={`text-sm mt-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    Deine Punkte werden deinem Account gutgeschrieben
                  </p>
                </div>
              ) : (
                /* Show login button if not logged in */
                <button
                  onClick={() => setShowAuthModal(true)}
                  className={`
                    w-full text-center py-2 px-4 rounded-lg font-medium
                    theme-transition
                    ${darkMode 
                      ? 'text-indigo-400 hover:bg-indigo-900/30' 
                      : 'text-indigo-600 hover:bg-indigo-50'}
                  `}
                >
                  {settings.studentRegistration === 'required' 
                    ? 'Anmelden oder Registrieren →'
                    : 'Bereits registriert? Anmelden →'
                  }
                </button>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className={`py-4 text-center text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
        Quiz System
      </footer>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        redirectAfterLogin={window.location.pathname}
      />
    </div>
  );
}

export default RegistrationScreen;
